import React, { useEffect, useState } from 'react';

import { PopoverProps } from '@mui/material/Popover';

import { ButtonProps } from '../Button/Button.jsx';
import { ChipProps } from '../Chip/Chip.jsx';
import Icon from '../Icon/Icon.jsx';
import Truncation from '../Truncation/Truncation.jsx';
import Typography from '../Typography/Typography.jsx';
import {
  IconContainer,
  StyledFilterChip,
  TextContainer,
} from './FilterChip.styles.jsx';
import FilterChipPopover from './FilterChipPopover.jsx';

export interface FilterChipProps extends ChipProps {
  innerRef?: React.Ref<HTMLDivElement>;
  onDelete?: () => void;
  filterLabel: string;
  chipValue: React.ReactNode;
  selected?: boolean;
  popoverContent?: React.ReactNode | null;
  popoverProps?: Partial<PopoverProps>;
  popoverTitle?: string;
  onClick?: (event: React.MouseEvent<Element>) => void;
  onClose?: () => void;
  onCancel?: () => void;
  withPopover?: boolean;
  cancelButtonProps?: Partial<ButtonProps>;
  saveButtonProps?: Partial<ButtonProps>;
  deletable?: boolean;
  truncatedChipValue?: boolean;
  chipValueTooltipText?: string;
  anchorEl?: Element | null;
  onAnchorElChange?: (newAnchorEl: Element | null) => void;
}

const FilterChip = ({
  innerRef,
  onDelete,
  filterLabel,
  chipValue,
  selected = false,
  onClick,
  withPopover = true,
  popoverContent,
  popoverProps,
  popoverTitle,
  saveButtonProps,
  cancelButtonProps,
  onClose,
  onCancel,
  truncatedChipValue = false,
  deletable = true,
  chipValueTooltipText,
  anchorEl: controlledAnchorEl,
  onAnchorElChange,
  ...props
}: FilterChipProps) => {
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  const handleClick = (event: React.MouseEvent<Element>) => {
    if (onClick) {
      onClick(event);
    }
    if (withPopover) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (controlledAnchorEl && withPopover) {
      setAnchorEl(controlledAnchorEl);
    }
  }, [controlledAnchorEl]);

  return (
    <>
      <StyledFilterChip ref={innerRef} {...props}>
        <TextContainer
          {...{ selected, deletable, truncatedChipValue }}
          onClick={handleClick}
        >
          <Typography
            variant="label01"
            color="text.secondary"
            sx={{ textWrap: 'nowrap', flexShrink: 0 }}
          >
            {filterLabel}
          </Typography>
          {truncatedChipValue ? (
            <Truncation
              variant="caption01"
              color="text.primary"
              tooltipTitle={chipValueTooltipText}
            >
              {chipValue}
            </Truncation>
          ) : (
            <Typography
              variant="caption01"
              color="text.primary"
              sx={{ flexGrow: '1' }}
            >
              {chipValue}
            </Typography>
          )}
        </TextContainer>
        {deletable ? (
          <IconContainer>
            <Icon
              component="Close"
              onClick={onDelete}
              sx={{ color: 'icon.secondary' }}
              data-testid="delete-chip-icon"
            />
          </IconContainer>
        ) : null}
      </StyledFilterChip>
      <FilterChipPopover
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onCancel={onCancel}
        popoverProps={{ anchorEl: anchorEl, ...popoverProps }}
        popoverContent={popoverContent}
        popoverTitle={popoverTitle}
        cancelButtonProps={cancelButtonProps}
        saveButtonProps={saveButtonProps}
      />
    </>
  );
};

export default FilterChip;
